import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/admin/EmployeesPage";
import { Plus, Pencil, Trash2, X, Loader2, Building2, MapPin } from "lucide-react";

const empty = { name: "", cliente_id: "", address: "", status: "em_curso", start_date: "" };
const statusMap = {
  em_curso: { label: "Em curso", cls: "bg-emerald-500/15 text-emerald-400" },
  pausada: { label: "Pausada", cls: "bg-amber-500/15 text-amber-400" },
  concluida: { label: "Concluída", cls: "bg-slate-500/20 text-slate-300" },
};

export default function ObrasPage() {
  const [list, setList] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () =>
    Promise.all([api.get("/obras"), api.get("/clientes")])
      .then(([o, c]) => {
        setList(o.data);
        setClientes(c.data);
      })
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, cliente_id: form.cliente_id || null };
      if (editing) await api.put(`/obras/${editing.id}`, payload);
      else await api.post("/obras", payload);
      toast.success(editing ? "Obra atualizada" : "Obra criada");
      setModal(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Eliminar ${o.name}?`)) return;
    await api.delete(`/obras/${o.id}`);
    toast.success("Obra eliminada");
    load();
  };

  return (
    <div data-testid="obras-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Obras</h1>
          <p className="text-muted-foreground">{list.length} obra(s).</p>
        </div>
        <button
          data-testid="add-obra-button"
          onClick={() => {
            setEditing(null);
            setForm(empty);
            setModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors"
        >
          <Plus className="w-4 h-4" /> Nova obra
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-7 h-7 text-primary animate-spin" />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((o) => {
            const st = statusMap[o.status] || statusMap.em_curso;
            return (
              <div key={o.id} data-testid={`obra-card-${o.id}`} className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 rounded-lg bg-primary/15 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                </div>
                <p className="font-semibold leading-tight">{o.name}</p>
                <p className="text-xs text-primary mt-0.5">{o.cliente_name || "Sem cliente"}</p>
                {o.address && (
                  <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> {o.address}
                  </p>
                )}
                <div className="mt-4 flex gap-2">
                  <button
                    data-testid={`edit-obra-${o.id}`}
                    onClick={() => {
                      setEditing(o);
                      setForm({ ...empty, ...o, cliente_id: o.cliente_id || "" });
                      setModal(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-secondary text-sm hover:bg-secondary/70 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button
                    data-testid={`delete-obra-${o.id}`}
                    onClick={() => remove(o)}
                    className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setModal(false)} />
          <form onSubmit={save} data-testid="obra-form" className="relative bg-card border border-border rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">{editing ? "Editar obra" : "Nova obra"}</h2>
              <button type="button" onClick={() => setModal(false)}>
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-3">
              <Field label="Nome da obra" testid="obra-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <div>
                <label className="block text-sm font-medium mb-1.5">Cliente</label>
                <select
                  data-testid="obra-cliente-select"
                  value={form.cliente_id}
                  onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary outline-none"
                >
                  <option value="">— Sem cliente —</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <Field label="Morada" testid="obra-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
              <div>
                <label className="block text-sm font-medium mb-1.5">Estado</label>
                <select
                  data-testid="obra-status-select"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary outline-none"
                >
                  <option value="em_curso">Em curso</option>
                  <option value="pausada">Pausada</option>
                  <option value="concluida">Concluída</option>
                </select>
              </div>
              <Field label="Data de início" testid="obra-start" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
            </div>
            <button data-testid="save-obra-button" disabled={saving} className="w-full mt-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
